<template>
  <div>
    <LeftSidebar
      v-if="isLeftSideBarAvailable"
      :is-open="isLeftSideBarToggled"
      @on-backdrop-click="isLeftSideBarToggled = false"
    />
    <RightSidebar
      :hub="hub"
      :is-open="isRightSideBarToggled"
      @on-backdrop-click="isRightSideBarToggled = false"
    />
    <TopNavigation
      :hub="hub"
      :is-left-side-bar-available="isLeftSideBarAvailable"
      :is-left-side-bar-toggled="isLeftSideBarToggled"
      :style="{
        top: topOffset,
      }"
      @toggle-right-sidebar="handleToggleRightSidebar"
      @toggle-left-sidebar="handleToggleLeftSidebar"
    />
    <MaintenanceBanner
      v-if="showMaintenanceBanner"
      class="fixed"
      @close="handleCloseBanner"
    />
  </div>

  <div
    class="layout-container iframe-bottom-inset pt-16"
    :class="[
      authUser.isLoggedIn ? 'md:pl-70 max-w-[100rem]' : 'max-w-[70rem]',
      { 'pt-60 md:pt-40': showMaintenanceBanner },
      { 'md:pt-16': !authUser.isLoggedIn },
      { 'pb-25 md:pb-0': isLaunchHubContentNavigationEnabled },
    ]"
  >
    <main class="layout-content">
      <OnboardingBanner
        v-if="
          organization?.isBusinessesEnabled &&
          organization?.isCoOp &&
          currentAccount &&
          currentAccount.currentlySelectedBusiness
        "
        :business="currentAccount.currentlySelectedBusiness"
        class="mb-5"
      />
      <slot />
    </main>
    <Footer v-if="!isInIframe" />
  </div>
</template>

<script setup lang="ts">
import { useIsInIframe } from '~/composables/use-is-in-iframe';
import { Footer } from '~/components/footer';
import { computed, ref, useIsMobile, useRuntimeConfig } from '#imports';
import { useAuthUserStore } from '~/store/auth-user';
import { useRoute } from 'nuxt/app';
import { TopNavigation } from '~/layouts/_components/top-navigation';
import { LeftSidebar } from '~/layouts/_components/left-sidebar';
import { RightSidebar } from '~/layouts/_components/right-sidebar';
import { MaintenanceBanner } from '~/layouts/_components/maintenance-banner';
import { MAINTENANCE_BANNER_HEIGHT } from '~/layouts/_components/maintenance-banner/maintenance-banner.constants';
import { apiGetSingleHub } from '~/api/hubs';
import { useAsyncData } from '#app';
import { OnboardingBanner } from '~/layouts/_components/onboarding-banner';
import { storeToRefs } from 'pinia';
import { useOrganizationStore } from '~/store/organization';
import { AccountType } from '~/api/accounts';
import { useAccountStore } from '~/store/account';

const authUser = useAuthUserStore();
const isInIframe = useIsInIframe();
const config = useRuntimeConfig();
const isMobile = useIsMobile();
const route = useRoute();
const orgSlug = computed<string>(() => route.params.organizationSlug as string);
const hubSlug = computed<string>(() => route.params.hubSlug as string);
const contentSlug = computed<string>(() => route.params.contentSlug as string);

const organizationStore = useOrganizationStore();
const { organization } = storeToRefs(organizationStore);
const { currentAccount } = storeToRefs(useAccountStore());

const { data: hub } = useAsyncData(
  `hub-${hubSlug.value}`,
  () => {
    if (!hubSlug.value) {
      return Promise.resolve(null);
    }
    return apiGetSingleHub(hubSlug.value);
  },
  { watch: [hubSlug] },
);

const maintenanceBannerCookie = useCookie<boolean | undefined>(
  'maintenance-banner',
  {
    secure: !import.meta.dev,
    sameSite: 'lax',
  },
);

const isLeftSideBarToggled = ref<boolean>(false);
const isRightSideBarToggled = ref<boolean>(false);
const isLeftSideBarAvailable = computed<boolean>(
  () =>
    !organization.value?.isPublic ||
    (Boolean(organization.value?.isPublic) && authUser.isLoggedIn),
);

const isContentDetails = computed(
  () => route.path === `/${hubSlug.value}/${contentSlug.value}`,
);

const isLaunchHubContentNavigationEnabled = computed(
  () =>
    currentAccount.value?.type === AccountType.STANDARD &&
    hub.value?.isLaunchEnabled &&
    isContentDetails.value,
);

const showMaintenanceBanner = computed(
  () =>
    config.public.isMaintenanceBannerEnabled &&
    maintenanceBannerCookie.value !== false,
);

const handleToggleRightSidebar = () => {
  isRightSideBarToggled.value = !isRightSideBarToggled.value;

  if (isLeftSideBarToggled.value) {
    isLeftSideBarToggled.value = false;
  }
};

const handleToggleLeftSidebar = () => {
  isLeftSideBarToggled.value = !isLeftSideBarToggled.value;

  if (isRightSideBarToggled.value) {
    isRightSideBarToggled.value = false;
  }
};

const handleCloseBanner = () => {
  maintenanceBannerCookie.value = false;
};

const topOffset = computed(() =>
  showMaintenanceBanner.value && !isMobile.value
    ? MAINTENANCE_BANNER_HEIGHT
    : 0,
);
</script>

<style scoped>
@reference "../assets/css/main.css";

.layout-container {
  @apply w-full relative flex flex-col min-h-screen mx-auto;
}

.layout-content {
  @apply flex-1 p-5;
}
</style>
